import { Button } from "@mui/material";
import jsPDF from "jspdf";
import { IconPrinter } from "@tabler/icons-react";
import {
  billingInfoLabels,
  billingRecipientTypeEnumMap,
  contactRolesEnumMap,
  legalTermsSchema,
  paymentMethodEnumMap,
  paymentTermsAndAgreement,
  PaymentTermsAndCondition,
  relationshipEnumMap,
} from "../enum/formEnum";
import { getPdfFormSections } from "./pdfFormSections";
import dataConstant from "../../constants/dataConstants";
import moment from "moment";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";

const GeneratePdf = ({
  formData,
  label,
  isPrintIcon = false,
  variant = "outlined",
}) => {
  const capitalizeFirst = (str) =>
    typeof str === "string" && str.length > 0
      ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
      : str;

  const parseHTMLFormatting = (line) => {
    const segments = [];
    const regex =
      /<([bi])><([bi])>(.*?)<\/\2><\/\1>|<([bi])>(.*?)<\/\4>|([^<]+)/gi;
    let match;

    while ((match = regex.exec(line)) !== null) {
      if (match[1] && match[2] && match[3]) {
        // <b><i>text</i></b> or <i><b>text</b></i>
        segments.push({ text: match[3], bold: true, italic: true });
      } else if (match[4] && match[5]) {
        // <b>text</b> or <i>text</i>
        const isBold = match[4].toLowerCase() === "b";
        const isItalic = match[4].toLowerCase() === "i";
        segments.push({ text: match[5], bold: isBold, italic: isItalic });
      } else if (match[6]) {
        // Plain text
        segments.push({ text: match[6], bold: false, italic: false });
      }
    }

    return segments.length > 0
      ? segments
      : [{ text: line, bold: false, italic: false }];
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    const topMargin = 16;
    const bottomMargin = 16;
    let yPosition = topMargin;

    const checkPageOverflow = (nextBlockHeight = 0) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      if (yPosition + nextBlockHeight > pageHeight - bottomMargin) {
        doc.addPage();
        yPosition = topMargin;
      }
    };

    const drawSectionHeader = (label) => {
      if (label) {
        checkPageOverflow(10);
        doc.setFontSize(18);
        doc.setTextColor(10, 10, 10);
        doc.setFont("helvetica", "bold");
        doc.text(label, margin, yPosition);
        yPosition += 10;
      }
    };

    const drawKeyValue = (leftLabel, leftValue, rightLabel, rightValue) => {
      checkPageOverflow();
      const labelFontSize = 10.5;
      const valueFontSize = 12;
      const lineSpacing = 5;

      const columnGap = 40;
      const colWidth = (pageWidth - margin * 2 - columnGap) / 2;

      const leftX = margin;
      const rightX = margin + colWidth + columnGap;

      const leftLabelText = `${leftLabel}:`;
      const leftValueText = leftValue?.toString() || "-";
      const leftLabelLines = doc.splitTextToSize(leftLabelText, colWidth);
      const leftValueLines = doc.splitTextToSize(leftValueText, colWidth);

      const rightLabelText = `${rightLabel}:`;
      const rightValueText = rightValue?.toString() || "-";
      const rightLabelLines = doc.splitTextToSize(rightLabelText, colWidth);
      const rightValueLines = doc.splitTextToSize(rightValueText, colWidth);

      const totalLinesLeft = leftLabelLines.length + leftValueLines.length;
      const totalLinesRight = rightLabelLines.length + rightValueLines.length;
      const totalLines = Math.max(totalLinesLeft, totalLinesRight);
      const blockHeight = totalLines * lineSpacing + 4;

      checkPageOverflow(blockHeight);

      // Left column
      if (leftLabel || leftValue) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text(leftLabelLines, leftX, yPosition);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(115, 115, 115);
        doc.text(
          leftValueLines,
          leftX,
          yPosition + leftLabelLines.length * lineSpacing
        );
      }

      // Right column
      if (rightLabel || rightValue) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text(rightLabelLines, rightX, yPosition);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(115, 115, 115);
        doc.text(
          rightValueLines,
          rightX,
          yPosition + rightLabelLines.length * lineSpacing
        );
      }

      yPosition += blockHeight;
    };

    const addKeyValueSection = (title, data) => {
      drawSectionHeader(title);
      for (let i = 0; i < data.length; i += 2) {
        const left = data[i];
        const right = data[i + 1] || ["", ""];
        drawKeyValue(left[0], left[1], right[0], right[1]);
      }
      yPosition += 8;
    };

    const addLegalTermsSection = async (title, data) => {
      drawSectionHeader(title);
      const fullWidth = pageWidth - margin * 2;
      const labelFontSize = 10.5;
      const valueFontSize = 12;
      const lineSpacing = 5;

      // Reset style context after header
      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);

      // Changed forEach to for...of to properly handle async/await
      for (const term of legalTermsSchema) {
        const value = formData?.[term.key]?.[term.fieldKey];
        const agreementText =
          value === true
            ? term.agreementText
            : term.key === "insurance"
            ? "Not Applicable"
            : `No, I do not agree to the ${term.title} terms`;

        // Split into lines (force full width)
        const titleLines = doc.splitTextToSize(term.title, fullWidth);
        const descriptionLines = term.description
          ? doc.splitTextToSize(term.description, fullWidth)
          : [];
        const agreementLines = doc.splitTextToSize(agreementText, fullWidth);

        // Estimate block height
        const blockHeight =
          (titleLines.length +
            descriptionLines.length +
            agreementLines.length +
            (term.key === "privacyConsent" ? 2 : 1)) *
            lineSpacing +
          10;

        checkPageOverflow(blockHeight);

        // === Title ===
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        titleLines.forEach((line) => {
          doc.text(line, margin, yPosition, {
            maxWidth: pageWidth - margin * 2,
            align: "left",
          });
          yPosition += lineSpacing;
        });
        yPosition += 2;

        // === Description ===
        if (term.description) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          doc.setTextColor(115, 115, 115);
          descriptionLines.forEach((line) => {
            doc.text(line, margin, yPosition, {
              maxWidth: pageWidth - margin * 2,
              align: "left",
            });
            yPosition += lineSpacing;
          });
          yPosition += 2;
        }

        // === Special case for Privacy Consent ===
        if (term.key === "privacyConsent") {
          const privacyText = `I agree with the storage and handling of my data by this website found at https://cambridgecaregivers.com/privacy-policy/.`;
          const privacyLines = doc.splitTextToSize(privacyText, fullWidth);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          doc.setTextColor(115, 115, 115);

          privacyLines.slice(0, -1).forEach((line) => {
            doc.text(line, margin, yPosition, {
              maxWidth: fullWidth,
              align: "left",
            });
            yPosition += lineSpacing;
          });

          // Last line = URL (highlight in red)
          doc.setTextColor(137, 7, 31);
          doc.text(privacyLines[privacyLines.length - 1], margin, yPosition, {
            maxWidth: fullWidth,
            align: "left",
          });
          yPosition += lineSpacing + 2;
        }

        if (term.key === "clientServicePlanAcknowledgement") {
          let displayWidth = 0;
          let displayHeight = 0;
          const columnGap = 40;
          const colWidth = (pageWidth - margin * 2 - columnGap) / 2;

          let signatureOfClient =
            value?.clientSignatureAndContactInfo?.signatureOfClient;

          if (signatureOfClient) {
            try {
              const img = new Image();
              img.crossOrigin = "Anonymous";
              img.src = signatureOfClient;

              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () =>
                  reject(new Error("Failed to load signature image"));
              });

              const aspectRatio = img.width / img.height;
              displayWidth = Math.min(img.width, colWidth);
              displayHeight = displayWidth / aspectRatio;

              if (displayHeight > 50) {
                displayHeight = 50;
                displayWidth = displayHeight * aspectRatio;
              }
            } catch (err) {
              console.error("Signature image error:", err);
            }

            const signatureOfClientBlockHeight =
              Math.max(displayHeight, 20) + 10;
            checkPageOverflow(signatureOfClientBlockHeight);
          }

          const signatureRowStartY = yPosition;
          const rightColumnX = margin + colWidth + columnGap;

          // Signature
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFontSize);
          doc.setTextColor(10, 10, 10);
          doc.text("Signature of Client or Client Contact", margin, yPosition);
          yPosition += lineSpacing;

          if (signatureOfClient && displayWidth > 0 && displayHeight > 0) {
            doc.addImage(
              signatureOfClient,
              "PNG",
              margin,
              yPosition,
              displayWidth,
              displayHeight
            );
          } else {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(valueFontSize);
            doc.setTextColor(115, 115, 115);
            doc.text("No signature provided", margin, yPosition);
          }

          // Right column: Date
          let rightColumnY = signatureRowStartY;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFontSize);
          doc.setTextColor(10, 10, 10);
          doc.text("Date", rightColumnX, rightColumnY);
          rightColumnY += lineSpacing;

          const formattedDate = value?.date
            ? new Date(value.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            : "-";

          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          doc.setTextColor(115, 115, 115);
          doc.text(formattedDate, rightColumnX, rightColumnY);

          // Move yPosition to after the signature image
          if (signatureOfClient && displayHeight > 0) {
            yPosition += displayHeight + 10;
          } else {
            yPosition += lineSpacing + 10;
          }

          // Printed Name
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFontSize);
          doc.setTextColor(10, 10, 10);
          doc.text("Printed Name of Client/Client Contact", margin, yPosition);

          yPosition += lineSpacing;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          doc.setTextColor(115, 115, 115);
          doc.text(value?.printedClientName || "-", margin, yPosition);
          yPosition += lineSpacing + 2;

          // Agency Representative

          const response = await apiClient.get(
            `${urlConstant.Submission.GetFileUrl}?fileName=${encodeURIComponent(
              dataConstant.ErnieSignImageUrl
            )}&expiryInMinutes=60`,
            {
              authRequired: true,
            }
          );

          const agencySignature = response?.data;

          if (agencySignature) {
            try {
              const agencyImg = new Image();
              agencyImg.crossOrigin = "Anonymous";
              agencyImg.src = agencySignature;

              //Wait for image to load
              await new Promise((resolve, reject) => {
                agencyImg.onload = resolve;
                agencyImg.onerror = (error) => {
                  console.error("Failed to load agency signature:", error);
                  reject(new Error(error));
                };
              });

              const agencyAspectRatio = agencyImg.width / agencyImg.height;
              let agencyDisplayWidth = Math.min(agencyImg.width, colWidth);
              let agencyDisplayHeight = agencyDisplayWidth / agencyAspectRatio;

              if (agencyDisplayHeight > 50) {
                agencyDisplayHeight = 50;
                agencyDisplayWidth = agencyDisplayHeight * agencyAspectRatio;
              }

              checkPageOverflow(agencyDisplayHeight);

              doc.addImage(
                agencyImg,
                "PNG",
                margin,
                yPosition,
                agencyDisplayWidth,
                agencyDisplayHeight
              );
              yPosition += agencyDisplayHeight + 5;
            } catch (err) {
              console.error("Agency signature image error:", err);
              doc.setFont("helvetica", "normal");
              doc.setFontSize(valueFontSize);
              doc.setTextColor(115, 115, 115);
              doc.text(
                "Agency Representative signature not provided",
                margin,
                yPosition
              );
              yPosition += lineSpacing + 2;
            }
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFontSize);
          doc.setTextColor(10, 10, 10);
          doc.text(
            [
              "___________________________________",
              "Ernie Cote",
              "Chief Operating Officer",
              "Agency Representative",
            ],
            margin,
            yPosition
          );
          yPosition += lineSpacing * 4 + 10;

          checkPageOverflow(10);

          continue;
        }

        // === Agreement Result ===
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(40, 48, 67);
        agreementLines.forEach((line) => {
          doc.text(line, margin, yPosition, {
            maxWidth: fullWidth,
            align: "left",
          });
          yPosition += lineSpacing;
        });
        yPosition += 6;
      }
    };

    const addPaymentDetailsSection = async (title, data) => {
      drawSectionHeader(title);
      const fullWidth = pageWidth - margin * 2;
      const labelFontSize = 10.5;
      const valueFontSize = 12;
      const lineSpacing = 5;
      const columnGap = 40;
      const colWidth = (pageWidth - margin * 2 - columnGap) / 2;

      let displayWidth = 0;
      let displayHeight = 0;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);

      const titleLines = doc.splitTextToSize("Base Rate (Per Hour)", fullWidth);
      const baseRateValue = formData?.paymentDetailsSection0?.baseRate;

      const blockHeight =
        (titleLines.length + baseRateValue.length + 2) * lineSpacing + 10;

      checkPageOverflow(blockHeight);

      // === Title ===
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.setTextColor(10, 10, 10);
      titleLines?.forEach((line) => {
        doc.text(line, margin, yPosition, {
          maxWidth: fullWidth,
          align: "left",
        });
        yPosition += lineSpacing;
      });

      if (baseRateValue) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(115, 115, 115);
        doc.text(`$ ${baseRateValue.toString()}`, margin, yPosition, {
          maxWidth: fullWidth,
          align: "left",
        });
        yPosition += 10;
      }

      const paymentSignatureUrl =
        formData?.paymentDetailsSection0?.signatureInfo?.signature;
      const paymentSignerNameValue =
        formData?.paymentDetailsSection0?.signatureInfo?.NameOfSigner;

      if (paymentSignatureUrl) {
        try {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = paymentSignatureUrl;

          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () =>
              reject(new Error("Failed to load signature image"));
          });

          const aspectRatio = img.width / img.height;
          displayWidth = Math.min(img.width, colWidth);
          displayHeight = displayWidth / aspectRatio;

          if (displayHeight > 50) {
            displayHeight = 50;
            displayWidth = displayHeight * aspectRatio;
          }
        } catch (err) {
          console.error("Signature image error:", err);
        }
      }
      const paymentSignatureBlockHeight = Math.max(displayHeight, 20) + 10;
      checkPageOverflow(paymentSignatureBlockHeight);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.setTextColor(10, 10, 10);
      doc.text("Payment Agreement", margin, yPosition, {
        maxWidth: fullWidth,
        align: "left",
      });
      yPosition += 2 + lineSpacing;

      const renderFormattedText = (text, startX, startY, maxWidth) => {
        let yPos = startY;

        // Remove all newlines to treat as continuous text
        const continuousText = text?.replace(/\n/g, " ").replace(/\s+/g, " ");

        const segments = parseHTMLFormatting(continuousText);
        let xPos = startX;
        let currentLine = [];
        let currentLineWidth = 0;

        segments?.forEach((segment) => {
          const words = segment?.text?.split(" ").filter((w) => w.length > 0);

          words?.forEach((word, wordIndex) => {
            // Add space before word (except first word of the line)
            const isFirstWordInLine = currentLine.length === 0;
            const textToRender = isFirstWordInLine ? word : " " + word;

            // Set font for measurement
            if (segment?.bold && segment?.italic) {
              doc.setFont("helvetica", "bolditalic");
            } else if (segment?.bold) {
              doc.setFont("helvetica", "bold");
            } else if (segment?.italic) {
              doc.setFont("helvetica", "italic");
            } else {
              doc.setFont("helvetica", "normal");
            }

            const wordWidth = doc.getTextWidth(textToRender);

            // Check if word fits on current line
            if (
              currentLineWidth + wordWidth > maxWidth &&
              currentLine.length > 0
            ) {
              // Render current line
              renderLine(currentLine, startX, yPos);
              yPos += lineSpacing;

              // Start new line with current word (no leading space)
              currentLine = [
                {
                  text: word,
                  bold: segment.bold,
                  italic: segment.italic,
                },
              ];

              // Measure without leading space for new line
              doc.setFont(
                "helvetica",
                segment.bold && segment.italic
                  ? "bolditalic"
                  : segment.bold
                  ? "bold"
                  : segment.italic
                  ? "italic"
                  : "normal"
              );
              currentLineWidth = doc.getTextWidth(word);
            } else {
              // Add word to current line
              currentLine.push({
                text: textToRender,
                bold: segment.bold,
                italic: segment.italic,
              });
              currentLineWidth += wordWidth;
            }
          });
        });

        // Render remaining text in current line
        if (currentLine.length > 0) {
          renderLine(currentLine, startX, yPos);
          yPos += lineSpacing;
        }

        return yPos;
      };

      // Function to render a line of formatted segments
      const renderLine = (segments, startX, yPos) => {
        let xPos = startX;

        segments?.forEach((segment) => {
          // Set font based on formatting
          if (segment.bold && segment.italic) {
            doc.setFont("helvetica", "bolditalic");
          } else if (segment.bold) {
            doc.setFont("helvetica", "bold");
          } else if (segment.italic) {
            doc.setFont("helvetica", "italic");
          } else {
            doc.setFont("helvetica", "normal");
          }

          doc.text(segment.text, xPos, yPos);
          xPos += doc.getTextWidth(segment.text);
        });
      };

      const paymentAgreementDescription =
        paymentTermsAndAgreement["description"];

      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);
      yPosition = renderFormattedText(
        paymentAgreementDescription,
        margin,
        yPosition,
        fullWidth - margin
      );
      yPosition += 2;

      if (paymentSignatureUrl) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text("Signature", margin, yPosition);

        doc.addImage(
          paymentSignatureUrl,
          "PNG",
          margin,
          yPosition + 5,
          displayWidth,
          displayHeight
        );
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text("Signature", margin, yPosition);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(115, 115, 115);
        doc.text("No signature provided", margin, yPosition + lineSpacing + 2);
      }

      // Right side: Name of Signer
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.setTextColor(10, 10, 10);
      doc.text("Name of Signer", margin + colWidth + columnGap, yPosition);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);
      doc.text(
        safeText(paymentSignerNameValue, "-"),
        margin + colWidth + columnGap,
        yPosition + lineSpacing + 2
      );

      yPosition += paymentSignatureBlockHeight;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.setTextColor(10, 10, 10);
      doc.text("Payment Method", margin, yPosition);
      yPosition += 6;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);
      doc.text(
        safeText(
          paymentMethodEnumMap[formData?.paymentDetailsSection0?.paymentMethod],
          "-"
        ),
        margin,
        yPosition
      );
      yPosition += 10;

      PaymentTermsAndCondition.forEach((term) => {
        const value =
          formData?.paymentDetailsSection0?.[term.fieldKey]?.[
            term.agreementKey
          ];
        if (
          formData?.paymentDetailsSection0?.paymentMethod &&
          formData?.paymentDetailsSection0?.paymentMethod === term.paymentMethod
        ) {
          const agreementText =
            value === true
              ? term.agreementText
              : `No, I do not agree to the ${term.title} terms`;

          const titleLines = doc.splitTextToSize(term.title, fullWidth);
          const descriptionLines = term.description
            ? doc.splitTextToSize(term.description, fullWidth)
            : [];
          const agreementLines = doc.splitTextToSize(agreementText, fullWidth);

          const blockHeight =
            (titleLines.length +
              descriptionLines.length +
              agreementLines.length +
              2) *
              lineSpacing +
            10;

          checkPageOverflow(blockHeight);

          // === Title ===
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFontSize);
          doc.setTextColor(10, 10, 10);
          titleLines.forEach((line) => {
            doc.text(line, margin, yPosition, {
              maxWidth: fullWidth,
              align: "left",
            });
            yPosition += lineSpacing;
          });
          yPosition += 2;

          // === Description ===
          if (term.description) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(valueFontSize);
            doc.setTextColor(115, 115, 115);
            descriptionLines.forEach((line) => {
              doc.text(line, margin, yPosition, {
                maxWidth: fullWidth,
                align: "left",
              });
              yPosition += lineSpacing;
            });
            yPosition += 2;
          }

          // === Agreement Result ===
          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          doc.setTextColor(40, 48, 67);
          agreementLines.forEach((line) => {
            doc.text(line, margin, yPosition, {
              maxWidth: fullWidth,
              align: "left",
            });
            yPosition += lineSpacing;
          });
          yPosition += 15;
        }
      });
    };

    const safeText = (value, fallback = "-") =>
      typeof value === "string" && value.trim() ? value : fallback;

    const addSignatureSection = async (title, data) => {
      drawSectionHeader(title);

      const [nameLabel, nameValue] = data[0];
      const signatureUrl = formData?.signatureInfo?.signature;

      const labelFontSize = 10.5;
      const valueFontSize = 12;
      const lineSpacing = 5;
      const columnGap = 40;
      const colWidth = (pageWidth - margin * 2 - columnGap) / 2;

      let displayWidth = 0;
      let displayHeight = 0;

      if (signatureUrl) {
        try {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = signatureUrl;

          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () =>
              reject(new Error("Failed to load signature image"));
          });

          const aspectRatio = img.width / img.height;
          displayWidth = Math.min(img.width, colWidth);
          displayHeight = displayWidth / aspectRatio;

          if (displayHeight > 50) {
            displayHeight = 50;
            displayWidth = displayHeight * aspectRatio;
          }
        } catch (err) {
          console.error("Signature image error:", err);
        }
      }

      const blockHeight = Math.max(displayHeight, 20) + 20;
      checkPageOverflow(blockHeight);

      // Left side: Signature image
      if (signatureUrl) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text("Signature", margin, yPosition);

        doc.addImage(
          signatureUrl,
          "PNG",
          margin,
          yPosition + 5,
          displayWidth,
          displayHeight
        );
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFontSize);
        doc.setTextColor(10, 10, 10);
        doc.text("Signature", margin, yPosition);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.setTextColor(115, 115, 115);
        doc.text("No signature provided", margin, yPosition + lineSpacing + 2);
      }

      // Right side: Name of Signer
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.setTextColor(10, 10, 10);
      doc.text(
        nameLabel || "Name of Signer",
        margin + colWidth + columnGap,
        yPosition
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueFontSize);
      doc.setTextColor(115, 115, 115);
      doc.text(
        nameValue || "-",
        margin + colWidth + columnGap,
        yPosition + lineSpacing + 2
      );

      yPosition += blockHeight + 6;
    };

    // ---- MAIN TITLE ----
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(171, 0, 51);
    const title = "Engagement Form Review";
    const titleWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(title, titleX, yPosition);
    yPosition += 3;

    doc.setFillColor(10, 10, 5);
    const underlineWidth = pageWidth * 0.25;
    const underlineX = (pageWidth - underlineWidth) / 2;
    doc.roundedRect(underlineX, yPosition, underlineWidth, 0.75, 0.5, 0.5, "F");
    yPosition += 12;

    // ---- FORM SECTIONS ----
    const formSections = getPdfFormSections(formData);

    // Authorized Contacts
    const authorizedContactSections = (formData?.contacts || []).map(
      (contact, index) => ({
        title: `Authorized Contact ${index + 1}`,
        data: [
          ["First Name", contact.contactFirstName || "-"],
          ["Last Name", contact.contactLastName || "-"],
          [
            "Relationship",
            relationshipEnumMap[contact.contactRelationship] || "-",
          ],
          ["Phone", contact.contactPhoneNumber || "-"],
          ["Email", contact.contactEmail || "-"],
          [
            "Role",
            Array.isArray(contact.contactRoles) &&
            contact.contactRoles.length > 0
              ? contact.contactRoles
                  .map(
                    (role) => contactRolesEnumMap[role] || capitalizeFirst(role)
                  )
                  .join(", ")
              : "-",
          ],
          ...(contact?.facilityAddress ||
          contact?.city ||
          contact?.state ||
          contact?.zipCode
            ? [
                [
                  "Address",
                  [
                    contact?.facilityAddress,
                    contact?.city,
                    contact?.state,
                    contact?.zipCode,
                  ]
                    .filter(Boolean)
                    .join(", "),
                ],
              ]
            : []),
        ],
      })
    );

    // Submission Details
    const submissionDateSection = {
      title: "",
      data: [
        [
          "Form submitted Date & Time",
          formData?.createdAt
            ? moment
                .utc(formData.createdAt)
                .tz(dataConstant.centralAmericaTimeZone)
                .format(dataConstant.dateFormatWithTime)
            : moment()
                .tz(dataConstant.centralAmericaTimeZone)
                .format(dataConstant.dateFormatWithTime),
        ],
      ],
    };

    // Signature handling
    const signatureIndex = formSections.findIndex(
      (s) => s.title === "Signature"
    );
    let signatureSection = null;
    if (signatureIndex !== -1) {
      signatureSection = formSections.splice(signatureIndex, 1)[0];
      formSections.splice(signatureIndex, 0, ...authorizedContactSections);
    } else {
      formSections.push(...authorizedContactSections);
    }

    // Append static sections
    formSections.push(
      {
        title: "Emergency Planning",
        data: [
          [
            "Priority Level",
            formData?.emergencyPlanningSection0?.priorityLevel || "-",
          ],
          [
            "STEAR (State of Texas Emergency Assistance Registry)",
            formData?.emergencyPlanningSection0?.stearRegistration === true
              ? "Yes"
              : "No",
          ],
          ["Notes", formData?.emergencyPlanningSection0?.notes || "-"],
        ],
      },
      {
        title: "Documentation",
        data: [
          [
            "Medical Power of Attorney (MPOA)",
            formData?.documentationSection0?.documentMPOA ? "Yes" : "No",
          ],
          [
            "OOH DNR (Do Not Resuscitate)",
            formData?.documentationSection0?.documentDNR ? "Yes" : "No",
          ],
          ...(formData?.documentationSection0?.documentDNR
            ? [
                [
                  "Enter Location of Document",
                  formData?.documentationSection0?.dnrLocation || "-",
                ],
              ]
            : []),
        ],
      },
      {
        title: "Payment Details",
        data: [],
      },
      {
        title: "Billing Information",
        data: Object.entries(billingInfoLabels)
          .filter(([key]) => {
            const valueExists = formData?.billingInfoSection0?.[key];
            const isRecipientOrSendMethod =
              key === "recipientType" || key === "sendMethod";
            const isAuthorizedContact =
              formData?.billingInfoSection0?.["recipientType"] ===
              "authorizedContact";
            return (
              valueExists && (isRecipientOrSendMethod || isAuthorizedContact)
            );
          })
          .map(([key, label]) => {
            const value = formData?.billingInfoSection0?.[key];
            return [
              label,
              key === "recipientType"
                ? billingRecipientTypeEnumMap[value] || value || "-"
                : value || "-",
            ];
          }),
      },
      {
        title: "Legal Terms & Agreements",
        data: [], // Data will be handled in addLegalTermsSection
      }
    );

    // Add signature
    if (signatureSection) {
      formSections.push(signatureSection);
    }

    // Add submission details
    formSections.push(submissionDateSection);

    // ---- RENDER PDF ----
    const processSections = async () => {
      for (const section of formSections) {
        if (section.title === "Signature") {
          await addSignatureSection(section.title, section.data);
        } else if (section.title === "Legal Terms & Agreements") {
          await addLegalTermsSection(section.title, section.data);
        } else if (section.title === "Payment Details") {
          await addPaymentDetailsSection(section.title, section.data);
        } else if (section.data && section.data.length) {
          addKeyValueSection(section.title, section.data);
        } else {
          drawSectionHeader(section.title);
        }
      }

      // Footer page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - margin,
          doc.internal.pageSize.getHeight() - 10,
          { align: "right" }
        );
      }

      doc.save(`engagement-form-${new Date().toISOString().split("T")[0]}.pdf`);
    };

    processSections();
  };

  return (
    <Button
      variant={variant}
      color="primary"
      onClick={generatePDF}
      sx={{ marginRight: "auto" }}
      startIcon={isPrintIcon ? <IconPrinter /> : ""}
      size="small"
    >
      {label}
    </Button>
  );
};

export default GeneratePdf;
